import { initialsFromName } from './initials.js';
import { formatDate } from './detailFormat.js';

/**
 * Pending suggestion row from a claim list item (`metadata.suggestions[]`).
 * Each entry carries `status` and `suggestion_uuid`; when present, `suggested_text`
 * is the proposed value for the Train Me diff (EVT-011).
 */
export function pendingSuggestionEntry(source = {}) {
  const suggestions = source.metadata?.suggestions ?? source.suggestions ?? [];
  return suggestions.find((s) => (s?.status ?? 'pending') === 'pending') ?? null;
}

/** Text of the pending suggestion, if any. */
export function pendingSuggestionText(source = {}) {
  const pending = pendingSuggestionEntry(source);
  return pending?.suggested_text ?? pending?.suggestedText ?? null;
}

function submitterFrom(event) {
  const owner = event.owner;
  if (owner?.name) {
    return {
      name: owner.name,
      role: owner.role ?? null,
      initials: owner.initials ?? initialsFromName(owner.name),
    };
  }
  const previewOwner = event.detail?.preview?.owner;
  const name = previewOwner?.fullName ?? previewOwner?.name ?? null;
  if (!name) return null;
  return {
    name,
    role: previewOwner?.role ?? null,
    initials: initialsFromName(name),
  };
}

function sourceMeta(detail, preview, event) {
  const source = detail.sourceDocument ?? preview.sourceDocument ?? null;
  if (source) {
    const sourceRecord =
      source.documentName ??
      [source.vaultName, source.folder].filter(Boolean).join(' / ') ??
      source.documentUuid ??
      null;
    const partition = [source.vaultName, source.folder, source.section].filter(Boolean).join(' / ') || null;
    return { sourceRecord, partition };
  }
  return {
    sourceRecord: detail.sandboxName ?? event.refLabel ?? null,
    partition: detail.sandboxName ?? null,
  };
}

function impactFrom(detail, preview) {
  const bundles = detail.linkedBundles ?? preview.linkedBundles ?? [];
  const names = bundles.map((b) => b?.bundleName ?? b?.bundleCode).filter(Boolean);
  if (!names.length) return null;
  return { agents: [], workflows: names };
}

/**
 * Maps an interim `claim_suggestion` inbox event into the Train Me detail shape
 * used by EVT-011 (`SubmissionBlock` / `EditBlock` / `ImpactBlock`).
 *
 * | EVT-011 (prototype)     | Backend source                                      |
 * |-------------------------|-----------------------------------------------------|
 * | submitter               | preview.owner / event.owner                         |
 * | note                    | detail.context / preview.context                    |
 * | submittedAt             | pending suggestion createdAt / detail.updatedAt     |
 * | fieldName               | event.title (list row)                              |
 * | currentValue            | detail.claimText (canonical claim)                  |
 * | proposedValue           | pending suggestion text, else claimText             |
 * | sourceRecord / partition| sourceDocument / sandboxName                        |
 * | version                 | latest versionHistory entry                         |
 * | impact.workflows        | linkedBundles names (agents not on the wire)        |
 */
export function mapClaimToTrainMeDetail(event) {
  const detail = event?.detail ?? {};
  const preview = detail.preview ?? {};
  const raw = detail.raw ?? {};
  const pending = pendingSuggestionEntry(raw) ?? pendingSuggestionEntry(detail);
  const suggestedText =
    detail.suggestedText ?? pendingSuggestionText(raw) ?? pendingSuggestionText(detail) ?? null;

  const claimText = detail.claimText ?? event?.situation ?? event?.description ?? '';
  const proposedValue = (suggestedText ?? claimText) || null;
  const currentValue =
    suggestedText && claimText && suggestedText.trim() !== claimText.trim() ? claimText : null;

  const versions = detail.versionHistory ?? preview.versionHistory ?? [];
  const latest = versions[0];
  const version =
    latest?.versionNumber != null
      ? `v${latest.versionNumber}`
      : latest?.version != null
        ? String(latest.version)
        : null;

  const { sourceRecord, partition } = sourceMeta(detail, preview, event);

  return {
    submitter: submitterFrom(event),
    submittedAt: formatDate(
      pending?.created_at ?? pending?.createdAt ?? detail.updatedAt ?? preview.updatedAt,
    ),
    note: detail.context ?? preview.context ?? detail.rationale ?? null,
    fieldName: event?.title ?? detail.title ?? preview.title ?? null,
    currentValue,
    proposedValue,
    sourceRecord,
    partition,
    version,
    impact: impactFrom(detail, preview),
    /** Baseline for promote-vs-suggest: accept when the edited value matches this. */
    baselineProposed: proposedValue,
  };
}
